/** Open-time scroll, plus the live composer clearance every floating piece of transcript chrome sits above, plus
 *  holding the reader's place across a dock↔full flip that reflows the column without remounting it. ChatBox never scrolls on mount by design, so Kelly owns landing on the newest message. */

import { useEffect, useLayoutEffect, useRef } from "react"
import { chatComposerClasses, chatMessageListClasses } from "@mui/x-chat"
import type { MessageListRootHandle } from "@mui/x-chat-headless"
import { COMPOSER_CLEARANCE_VAR } from "./composer-chrome"

/**
 * How long the pin follows the transcript after a thread opens. A single absolute window, deliberately not an
 * idle timer: growth restarting an idle timer never lets go during a stream, and an idle timer short enough to
 * be safe expires before late reflows (measured: a 43px settle landing ~600ms after the rows).
 */
export const TRANSCRIPT_PIN_DEADLINE_MS = 1500
/** Direction-checked early-out: an upward wheel lands before its scroll event does. Everything else — touch,
 *  scrollbar, keyboard, find-in-page — is left to the scroll position, which is the authoritative signal. */
export const READER_TAKEOVER_EVENT = "wheel" as const

export interface TranscriptPinOptions {
  scroller: HTMLElement
  /** Observed alongside the scroller, whose own height changes when chrome above ChatBox mounts. */
  content: Element | null
  /** The library's scrollToBottom — a raw scrollTop write leaves isAtBottom stale for a frame and flashes the affordance. */
  scrollToBottom: () => void
  deadlineMs?: number
}

/**
 * Hold the transcript at its newest message until the deadline passes or the reader scrolls up. Takeover is read
 * from the scroll position rather than from input events: find-in-page, assistive-technology navigation and (on
 * some browsers) a scrollbar drag move the transcript without dispatching anything we could listen for.
 */
export function pinToBottomAfterOpen(options: TranscriptPinOptions): () => void {
  const { scroller, content, scrollToBottom, deadlineMs = TRANSCRIPT_PIN_DEADLINE_MS } = options
  let isReleased = false
  let markTop = scroller.scrollTop
  let growthObserver: ResizeObserver | undefined

  const maxReachableTop = () => Math.max(0, scroller.scrollHeight - scroller.clientHeight)

  /**
   * Takeover is movement AWAY from the newest message, measured against a high-water mark rather than against the
   * previous position: a slow scrollbar drag moves under a pixel per event, and a mark that follows it down would
   * let the reader travel any distance undetected. The mark falls only when the bottom itself moves up — a
   * shrinking transcript or a growing viewport clamps scrollTop with no reader involved, and comparing raw offsets
   * read that as takeover (the clearance measurement landing after the pin does exactly this).
   */
  const hasReaderScrolledUp = () => {
    markTop = Math.min(markTop, maxReachableTop())
    return scroller.scrollTop < markTop - 1
  }

  const onScroll = () => {
    if (hasReaderScrolledUp()) {
      release()
      return
    }
    markTop = Math.max(markTop, scroller.scrollTop)
  }

  // Direction matters here for the same reason it does on scroll: pinned at the bottom, a downward wheel and the
  // overscroll it produces fire without moving scrollTop at all, so releasing on any wheel drops the pin early.
  // The literal event name narrows addEventListener's overload, so deltaY is real here rather than asserted.
  const onWheel = (event: WheelEvent) => {
    if (event.deltaY < 0) release()
  }

  function release() {
    if (isReleased) return
    isReleased = true
    clearTimeout(deadlineTimer)
    growthObserver?.disconnect()
    scroller.removeEventListener("scroll", onScroll)
    scroller.removeEventListener(READER_TAKEOVER_EVENT, onWheel)
  }

  const pin = () => {
    scrollToBottom()
    // Never lower the mark from our own action: a scrollTo issued from a resize callback lands a frame later,
    // so the value read straight back can still be the pre-scroll one.
    markTop = Math.max(markTop, scroller.scrollTop)
  }

  const deadlineTimer = setTimeout(release, deadlineMs)
  pin()
  scroller.addEventListener("scroll", onScroll, { passive: true })
  scroller.addEventListener(READER_TAKEOVER_EVENT, onWheel, { passive: true })

  if (typeof ResizeObserver !== "undefined") {
    growthObserver = new ResizeObserver(() => {
      if (isReleased) return
      // Checked here too, not just on scroll: a resize is delivered before the scroll event of the same frame,
      // which is the path that yanked a reader who had just grabbed the scroll bar.
      if (hasReaderScrolledUp()) {
        release()
        return
      }
      pin()
    })
    // border-box: a padding change on the content alters scrollHeight without touching its content box.
    growthObserver.observe(scroller, { box: "border-box" })
    if (content != null) growthObserver.observe(content, { box: "border-box" })
  }

  return release
}

/** `@mui/x-chat-headless`'s `MessageListRoot` stamps this on every row, unconditionally, alongside `data-message-id`. */
export const MESSAGE_ROW_SELECTOR = "[data-message-list-row]"

/**
 * How close to the newest message still counts as being ON it. This must be **the same number the library
 * uses**, and it is: `conversation-surface.tsx` passes it to `ChatMessageList` as `autoScroll.buffer`, which
 * is what `useMessageListBehavior` compares against for `isAtBottom`.
 *
 * Two numbers here is a bug, not an inconsistency. The library's default is 150px, so a reader 100px off the
 * bottom is one the library believes is pinned and will keep auto-scrolling through a stream — while a 1px
 * epsilon here would capture them as a mid-transcript anchor and top-anchor them somewhere else. Whichever of
 * the two writes last wins, and which one that is depends on the event loop. 150 is the library's number
 * rather than ours because it is the one with behaviour attached (following a live answer); we pass it
 * explicitly so it cannot drift if the library changes its default.
 */
export const TRANSCRIPT_AT_BOTTOM_BUFFER_PX = 150

export type TranscriptAnchor =
  | { kind: "bottom" }
  | { kind: "message", messageId: string, offsetTop: number }

/**
 * What the reader is looking at, in terms that survive a reflow. Deliberately NOT a scroll ratio: the flip
 * rewraps code blocks, tables and artifact cards by different amounts, so a ratio drifts by whole messages on a
 * long transcript. "At the bottom" is the common case and is exact; otherwise the topmost visible row and how
 * far it sits above the fold.
 */
export function captureTranscriptAnchor(scroller: HTMLElement): TranscriptAnchor {
  if (scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight <= TRANSCRIPT_AT_BOTTOM_BUFFER_PX) return { kind: "bottom" }
  const scrollerTop = scroller.getBoundingClientRect().top
  const rows = Array.from(scroller.querySelectorAll<HTMLElement>(MESSAGE_ROW_SELECTOR))
  const row = rows.find((candidate) => candidate.getBoundingClientRect().bottom > scrollerTop)
  const messageId = row?.getAttribute("data-message-id")
  if (row == null || messageId == null) return { kind: "bottom" }
  return { kind: "message", messageId, offsetTop: row.getBoundingClientRect().top - scrollerTop }
}

/**
 * Put the reader back, and leave the library agreeing about where they are.
 *
 * The bottom case goes through the library's `scrollToBottom`, which is safe on its own terms: it updates
 * `isAtBottom` and re-captures the library's anchor synchronously (and a raw write there would leave
 * `isAtBottom` stale for a frame and flash the jump-to-latest affordance).
 *
 * A mid-transcript anchor has no library equivalent, so it is a relative `scrollTop` write — and that write is
 * behind the library's back. `useMessageListBehavior`'s row-resize rAF calls `restoreAnchor(anchorRef.current)`
 * BEFORE it re-captures, so a stale anchor from before the reflow would drag the transcript by the anchor row's
 * height change: the exact drift this exists to prevent. The `scroll` event the write queues would usually
 * re-seed it in time — "usually" being a spec ordering nobody wrote down here, and no event at all when the
 * delta rounds to zero (a reader near the top of the transcript, deterministically). So the library's own
 * handler is invoked synchronously instead, unconditionally. It is idempotent: the real event that may follow
 * re-captures the same position.
 */
export function restoreTranscriptAnchor(scroller: HTMLElement, anchor: TranscriptAnchor, scrollToBottom: () => void): void {
  if (anchor.kind === "bottom") {
    scrollToBottom()
    return
  }
  const rows = Array.from(scroller.querySelectorAll<HTMLElement>(MESSAGE_ROW_SELECTOR))
  // No index fallback: `data-message-id` is stamped unconditionally, so a miss means the id CHANGED, and the
  // row at that index is then an unrelated message. A confident restore to the wrong place is worse than none.
  const row = rows.find((candidate) => candidate.getAttribute("data-message-id") === anchor.messageId)
  if (row == null) return
  scroller.scrollTop += row.getBoundingClientRect().top - scroller.getBoundingClientRect().top - anchor.offsetTop
  scroller.dispatchEvent(new Event("scroll"))
}

/**
 * Wires the conversation column: `containerRef` on the positioned box hosting ChatBox and its floating chrome,
 * `listRef` on ChatBox's message list so the pin drives the library's own scrollToBottom. `viewKey` names the
 * geometry the transcript column is laid out in — the dock's view AND the conversations chrome beside it, since
 * both are siblings in the same flex row and either one moving reflows the column without remounting anything.
 */
export function useTranscriptScroll(navKey: string, viewKey: string) {
  const containerRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<MessageListRootHandle>(null)

  // Layout effect: a passive effect lets the browser paint the top of a long transcript before the jump.
  useLayoutEffect(() => {
    const scroller = containerRef.current?.querySelector<HTMLElement>(`.${chatMessageListClasses.scroller}`)
    if (scroller == null) return undefined
    return pinToBottomAfterOpen({
      scroller,
      content: scroller.querySelector(`.${chatMessageListClasses.content}`),
      scrollToBottom: () => listRef.current?.scrollToBottom({ behavior: "auto" }),
    })
  }, [navKey])

  /**
   * Hold the reader's place across a geometry change.
   *
   * The baseline is captured from SCROLL EVENTS, not at the moment of the change. Capturing in the effect's
   * cleanup was tried and is wrong: React commits a fiber's child mutations before running that fiber's own
   * layout-effect cleanup, so seating the conversations drawer — a child of this surface — reflows the column
   * BEFORE the capture measures it, while the dock's own width mutation (an ancestor) has not landed yet. The
   * capture then reads a layout that never existed on screen: the drawer's 264px taken out of a 392px dock.
   * Measured, expanding from docked: a reader parked exactly at the newest message captured as 926px above it.
   *
   * A scroll event is a moment when the DOM is settled and the reader has just told us where they are, which is
   * also where `@mui/x-chat-headless` takes its own anchor. Coalesced to one capture per frame — the walk is
   * O(rows above the fold) and scroll events are not rate-limited.
   *
   * The listener sits on the CONTAINER in the capture phase, not on the scroller: `scroll` does not bubble, and
   * binding the scroller would tie this effect's lifetime to an element it has to find at mount. A scroller that
   * arrives later — a lazy or conditionally-rendered ChatBox — would otherwise never be listened to, and the
   * hook would degrade silently to "always jump to newest".
   */
  const anchorRef = useRef<TranscriptAnchor>({ kind: "bottom" })
  useLayoutEffect(() => {
    const container = containerRef.current
    if (container == null) return undefined
    const findScroller = () => container.querySelector<HTMLElement>(`.${chatMessageListClasses.scroller}`)
    // Seeded eagerly so the first flip has a baseline even if the reader has not scrolled. No transcript on
    // screen means no place to lose — the newest message, which is where the open-time pin lands too.
    const initial = findScroller()
    anchorRef.current = initial == null ? { kind: "bottom" } : captureTranscriptAnchor(initial)
    let frame = 0
    const onScroll = () => {
      if (frame !== 0) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const scroller = findScroller()
        if (scroller != null) anchorRef.current = captureTranscriptAnchor(scroller)
      })
    }
    container.addEventListener("scroll", onScroll, { capture: true, passive: true })
    return () => {
      if (frame !== 0) cancelAnimationFrame(frame)
      container.removeEventListener("scroll", onScroll, { capture: true })
    }
  }, [navKey])

  // Restore. No thread guard: the baseline effect above is declared first and re-seeds on every navKey change,
  // so a stamp comparing them could never be false — and the open-time pin owns a thread change's landing, so
  // this deliberately does not key on navKey at all.
  useLayoutEffect(() => {
    const scroller = containerRef.current?.querySelector<HTMLElement>(`.${chatMessageListClasses.scroller}`)
    if (scroller == null) return
    restoreTranscriptAnchor(scroller, anchorRef.current, () => listRef.current?.scrollToBottom({ behavior: "auto" }))
  }, [viewKey])

  // The composer card grows with the question, so floating chrome is positioned off its measured top edge rather
  // than off CONVERSATION_BOTTOM_PADDING — that constant is a lower bound, not the card's height.
  useEffect(() => {
    const container = containerRef.current
    const composer = container?.querySelector<HTMLElement>(`.${chatComposerClasses.root}`)
    if (container == null || composer == null) return undefined
    let previousClearance: number | undefined
    const measure = () => {
      const scroller = container.querySelector<HTMLElement>(`.${chatMessageListClasses.scroller}`)
      const anchor = scroller == null ? undefined : captureTranscriptAnchor(scroller)
      const clearance = Math.max(0, Math.round(container.getBoundingClientRect().bottom - composer.getBoundingClientRect().top))
      container.style.setProperty(COMPOSER_CLEARANCE_VAR, `${clearance}px`)
      if (anchor?.kind === "bottom" && previousClearance != null && clearance !== previousClearance) listRef.current?.scrollToBottom({ behavior: "auto" })
      previousClearance = clearance
    }
    measure()
    if (typeof ResizeObserver === "undefined") return () => container.style.removeProperty(COMPOSER_CLEARANCE_VAR)
    const clearanceObserver = new ResizeObserver(measure)
    clearanceObserver.observe(composer)
    return () => {
      clearanceObserver.disconnect()
      container.style.removeProperty(COMPOSER_CLEARANCE_VAR)
    }
  }, [navKey])

  return { containerRef, listRef }
}
